import React, { useMemo, useState, useCallback } from 'react';
import { Box, Chip, Collapse, Typography, IconButton, Tooltip, useTheme } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  ContentCopy as ContentCopyIcon, Check as CheckIcon,
} from '@mui/icons-material';
import { LogEntry, LEVEL_COLORS } from '../types';
import { fmtLogTimestamp } from '../utils';

interface LogRowProps {
  entry: LogEntry;
  isExpanded: boolean;
  isOdd: boolean;
  isCompact: boolean;
  highlightTerm: string;
  onToggle: (id: number) => void;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 4,
  paddingBottom: 4,
  cursor: 'pointer',
  borderLeft: '3px solid',
  transition: 'background-color 0.15s',
};

const timestampStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '0.72rem',
  color: '#888',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  lineHeight: 1.8,
  userSelect: 'text',
};

const levelBadgeBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 42,
  paddingLeft: 6,
  paddingRight: 6,
  paddingTop: 1,
  paddingBottom: 1,
  borderRadius: 4,
  flexShrink: 0,
};

const levelTextStyle: React.CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: '#fff',
  textTransform: 'uppercase',
  lineHeight: 1.5,
  letterSpacing: 0.3,
};

const expandIconStyle: React.CSSProperties = {
  fontSize: 16,
  color: '#999',
  flexShrink: 0,
  marginTop: 2,
};

const collapsedLineStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  lineHeight: 1.8,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
  userSelect: 'text',
};

const expandedLineStyle: React.CSSProperties = {
  ...collapsedLineStyle,
  textOverflow: 'unset',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

function renderHighlighted(text: string, term: string, isDark: boolean): React.ReactNode {
  if (!term) return text;
  const lower = text.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let matchIdx = lower.indexOf(term, cursor);
  let key = 0;
  while (matchIdx !== -1) {
    if (matchIdx > cursor) parts.push(text.slice(cursor, matchIdx));
    parts.push(
      <mark
        key={key++}
        style={{
          backgroundColor: isDark ? '#b8860b' : '#ffe066',
          color: isDark ? '#fff' : '#000',
          borderRadius: 2,
          padding: '0 2px',
        }}
      >
        {text.slice(matchIdx, matchIdx + term.length)}
      </mark>,
    );
    cursor = matchIdx + term.length;
    matchIdx = lower.indexOf(term, cursor);
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

const LogRowInner: React.FC<LogRowProps> = ({ entry, isExpanded, isOdd, isCompact, highlightTerm, onToggle }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const timestamp = useMemo(() => fmtLogTimestamp(entry.timestamp), [entry.timestamp]);
  const borderColor = LEVEL_COLORS[entry.level] || LEVEL_COLORS.unknown;
  const bgDefault = isOdd ? 'rgba(128,128,128,0.04)' : 'transparent';
  const effectiveExpanded = isCompact ? false : isExpanded;
  const isHighlighted = !!highlightTerm && entry.line.toLowerCase().includes(highlightTerm);
  const highlightBg = isDark ? 'rgba(184,134,11,0.18)' : 'rgba(255,224,102,0.28)';
  const rowBg = isHighlighted ? highlightBg : bgDefault;
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  const renderedLine = useMemo(
    () => renderHighlighted(entry.line, highlightTerm, isDark),
    [entry.line, highlightTerm, isDark],
  );

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(entry.line);
    } catch {
      // Fallback for non-secure contexts where the Clipboard API is unavailable.
      const ta = document.createElement('textarea');
      ta.value = entry.line;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }, [entry.line]);

  const showCopyButton = hovered || copied || isCompact;

  return (
    <div>
      <div
        onClick={() => onToggle(entry.id)}
        style={{
          ...rowStyle,
          borderLeftColor: borderColor,
          backgroundColor: rowBg,
        }}
        onMouseEnter={(e) => { setHovered(true); e.currentTarget.style.backgroundColor = isHighlighted ? highlightBg : 'rgba(128,128,128,0.08)'; }}
        onMouseLeave={(e) => { setHovered(false); e.currentTarget.style.backgroundColor = rowBg; }}
      >
        <span style={timestampStyle}>{timestamp}</span>

        <span style={{ ...levelBadgeBase, backgroundColor: borderColor }}>
          <span style={levelTextStyle}>
            {entry.level === 'unknown' ? '???' : entry.level}
          </span>
        </span>

        <span style={effectiveExpanded ? expandedLineStyle : collapsedLineStyle}>{renderedLine}</span>

        <Tooltip title={copied ? 'Copied!' : 'Copy log line'} placement="top" arrow>
          <IconButton
            size="small"
            onClick={handleCopy}
            aria-label="Copy log line to clipboard"
            sx={{
              p: 0.25,
              mt: '-1px',
              flexShrink: 0,
              color: copied ? 'success.main' : 'text.secondary',
              opacity: showCopyButton ? 1 : 0,
              pointerEvents: showCopyButton ? 'auto' : 'none',
              transition: 'opacity 0.15s',
              '&:hover': { color: copied ? 'success.main' : 'text.primary', backgroundColor: 'action.hover' },
            }}
          >
            {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>

        {!isCompact && (
          effectiveExpanded
            ? <ExpandLessIcon style={expandIconStyle} />
            : <ExpandMoreIcon style={expandIconStyle} />
        )}
      </div>

      {effectiveExpanded && (
        <Collapse in>
          <Box
            sx={{
              px: 2, py: 1, ml: '3px',
              borderLeft: '3px solid',
              borderLeftColor: borderColor,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, display: 'block', mb: 0.75, fontSize: '0.72rem' }}
            >
              Labels
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Object.entries(entry.labels).map(([k, v]) => (
                <Chip
                  key={k}
                  label={<span><strong>{k}</strong>={v}</span>}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem', fontFamily: 'monospace', borderColor: 'text.disabled' }}
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      )}
    </div>
  );
};

export const LogRow = React.memo(LogRowInner, (prev, next) => {
  return prev.entry.id === next.entry.id
    && prev.isExpanded === next.isExpanded
    && prev.isOdd === next.isOdd
    && prev.isCompact === next.isCompact
    && prev.highlightTerm === next.highlightTerm;
});

LogRow.displayName = 'LogRow';
